import { defineMessages, useIntl } from 'react-intl';
import type { UseFormReturn } from 'react-hook-form';
import { validatePassword } from '../../../../utils/password-utils';
import type { FieldConfig } from '../../../../components/field';

export interface ResetPasswordFormValues {
    password: string;
}

const messages = defineMessages({
    newPassword: {
        defaultMessage: 'New Password',
        id: 'account.reset-password.password',
    },
    required: {
        defaultMessage: 'Password is required',
        id: 'account.reset-password.error.required-password',
    },
    minimumCharacters: {
        defaultMessage: 'Password must contain at least 6 characters.',
        id: 'account.reset-password.error.minimum-characters',
    },
    anyLetter: {
        defaultMessage: 'Password must contain at least one letter.',
        id: 'account.reset-password.error.any-letter',
    },
    containNumber: {
        defaultMessage: 'Password must contain at least one number.',
        id: 'account.reset-password.error.contain-number',
    },
});

export function useResetPasswordFields(
    form: UseFormReturn<ResetPasswordFormValues>,
): Record<string, FieldConfig> {
    const {
        control,
        formState: { errors },
    } = form;
    const intl = useIntl();

    const fields: Record<string, FieldConfig> = {
        password: {
            id: 'reset-password-password-input',
            name: 'password',
            label: intl.formatMessage(messages.newPassword),
            type: 'password',
            defaultValue: '',
            rules: {
                required: intl.formatMessage(messages.required),
                validate: {
                    hasMinChars: (val: string): string | true =>
                        validatePassword(val).hasMinChars ||
                        intl.formatMessage(messages.minimumCharacters),
                    hasAnyLetter: (val: string): string | true =>
                        validatePassword(val).hasAnyLetter ||
                        intl.formatMessage(messages.anyLetter),
                    hasNumber: (val: string): string | true =>
                        validatePassword(val).hasNumber ||
                        intl.formatMessage(messages.containNumber),
                },
            },
            error: errors.password,
            control,
            variant: 'new-style',
        },
    };

    return fields;
}
